import { useEffect, useState } from "react";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Paper,
	Stack,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import CampaignRounded from "@mui/icons-material/CampaignRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import FileDownloadRounded from "@mui/icons-material/FileDownloadRounded";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import AddCircleOutlineRounded from "@mui/icons-material/AddCircleOutlineRounded";
import GigaAdPreview from "./GigaAdPreview";

const DEFAULT_STYLE_GUIDE = `Google Search RSA
Headlines: up to 15, max 30 characters each. Unique, keyword-aware, action-oriented.
Descriptions: exactly 4, max 90 characters each. Benefit + CTA. No placeholders.`;

const AD_PREVIEW_WIDTH = {
	mobile: "min(360px, 100%)",
	tablet: "min(720px, 100%)",
	pc: "100%",
};

const quietBtnSx = {
	textTransform: "none",
	fontWeight: 600,
	fontSize: 12.5,
	borderRadius: 1.5,
};

const previewToggleSx = {
	textTransform: "none",
	fontWeight: 600,
	fontSize: 12.5,
	px: 1.25,
	py: 0.4,
	color: grey[700],
	borderColor: grey[300],
	"&.Mui-selected": {
		bgcolor: grey[100],
		color: grey[900],
		borderColor: grey[400],
	},
	"&.Mui-selected:hover": { bgcolor: grey[200] },
};

const fieldSx = {
	"& .MuiOutlinedInput-root": {
		bgcolor: "#fff",
		borderRadius: 1.5,
		fontSize: 13.5,
	},
};

const formatCampaignsMarkdown = (campaigns = []) =>
	campaigns
		.map((campaign) => {
			let text = `Campaign: ${campaign.campaignName}\n`;
			(campaign.adGroups || []).forEach((group) => {
				text += `\nAd Group: ${group.name}\n`;
				if (group.keywords?.length) text += `Keywords: ${group.keywords.join(", ")}\n`;
				(group.ads || []).forEach((ad, index) => {
					text += `\nAd ${index + 1}:\n`;
					if (ad.headlines?.length) text += `  Headlines: ${ad.headlines.join(" | ")}\n`;
					if (ad.descriptions?.length) text += `  Descriptions: ${ad.descriptions.join(" | ")}\n`;
				});
			});
			return text;
		})
		.join(`\n\n${"-".repeat(40)}\n\n`);

const slug = (value = "") =>
	String(value)
		.trim()
		.toLowerCase()
		.replace(/\s+/g, "-")
		.replace(/[^a-z0-9-]/g, "");

const GigaCampaignsView = ({ run, busy, onGenerate, onCreateAds, onReset }) => {
	const campaigns = run?.campaigns || [];
	const creation = run?.campaign_creation || {};
	const createdAds = creation.ads || [];
	const generating = busy === "campaigns";
	const creatingAds = busy === "campaigns-create";
	const hasExplore = Boolean(run?.ideas?.length);
	const [brandName, setBrandName] = useState(run?.brand_name || "");
	const [styleGuide, setStyleGuide] = useState(creation.styleGuide || DEFAULT_STYLE_GUIDE);
	const [adExamples, setAdExamples] = useState("");
	const [creationKeywords, setCreationKeywords] = useState(creation.keywords || "");
	const [instructions, setInstructions] = useState(creation.instructions || "");
	const [adPreview, setAdPreview] = useState("pc");
	const [info, setInfo] = useState("");

	useEffect(() => {
		setBrandName(run?.brand_name || "");
	}, [run?.brand_name]);

	useEffect(() => {
		if (creation.keywords) setCreationKeywords(creation.keywords);
		if (creation.instructions) setInstructions(creation.instructions);
		if (creation.styleGuide) setStyleGuide(creation.styleGuide);
	}, [creation.keywords, creation.instructions, creation.styleGuide]);

	const downloadCampaigns = () => {
		if (!campaigns.length) return;
		const blob = new Blob([formatCampaignsMarkdown(campaigns)], { type: "text/markdown;charset=utf-8" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `${String(run?.name || "giga-campaigns").replace(/[^\w\-]+/g, "_").slice(0, 40)}_campaigns.md`;
		link.click();
		URL.revokeObjectURL(url);
	};

	if (!hasExplore) {
		return <Alert severity="info">Run Explore before generating campaigns.</Alert>;
	}

	return (
		<Stack spacing={2}>
			<Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
				<Box>
					<Typography fontWeight={800} fontSize={18} color={grey[900]}>
						Campaigns
					</Typography>
					<Typography variant="body2" color="text.secondary">
						RSA drafts from this run’s keyword data. Nothing is written to Google Ads.
					</Typography>
				</Box>
				<Tooltip title="Reset Campaigns">
					<span>
						<IconButton
							size="small"
							onClick={onReset}
							disabled={generating || creatingAds || (!campaigns.length && !createdAds.length)}
							sx={{ color: grey[700] }}>
							<DeleteOutlineRounded fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
			</Stack>

			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
					gap: 1.5,
				}}>
				<TextField
					size="small"
					label="Google Ads account"
					value={run?.ads_account?.name ? `${run.ads_account.name} (${run.ads_account.customerId || "—"})` : "—"}
					InputProps={{
						readOnly: true,
						endAdornment: (
							<IconButton size="small" onClick={() => setInfo("account")}>
								<InfoOutlined sx={{ fontSize: 16, color: grey[500] }} />
							</IconButton>
						),
					}}
					sx={fieldSx}
				/>
				<TextField
					size="small"
					label="Brand name (optional)"
					value={brandName}
					onChange={(e) => setBrandName(e.target.value)}
					placeholder="Rare Rabbit"
					InputProps={{
						endAdornment: (
							<IconButton size="small" onClick={() => setInfo("brand")}>
								<InfoOutlined sx={{ fontSize: 16, color: grey[500] }} />
							</IconButton>
						),
					}}
					sx={fieldSx}
				/>
			</Box>

			<Accordion disableGutters elevation={0} sx={{ border: `1px solid ${grey[200]}`, borderRadius: "8px !important" }}>
				<AccordionSummary expandIcon={<ExpandMoreRounded />}>
					<Typography fontWeight={600} fontSize={13.5} color="#175CD3">
						Advanced configuration
					</Typography>
				</AccordionSummary>
				<AccordionDetails>
					<Stack spacing={1.5}>
						<TextField
							multiline
							minRows={4}
							label="Style guide"
							value={styleGuide}
							onChange={(e) => setStyleGuide(e.target.value)}
							sx={fieldSx}
						/>
						<TextField
							multiline
							minRows={3}
							label="Prompt / historical ad examples (optional)"
							value={adExamples}
							onChange={(e) => setAdExamples(e.target.value)}
							placeholder="Paste RSA examples or extra prompt text. GIGA repo pulls this from Ads; here it is optional paste-in."
							sx={fieldSx}
						/>
					</Stack>
				</AccordionDetails>
			</Accordion>

			<Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
				<Button
					variant="contained"
					startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <CampaignRounded />}
					onClick={() => onGenerate({ brandName, styleGuide, adExamples })}
					disabled={generating || creatingAds}
					sx={{ ...quietBtnSx, textTransform: "none" }}>
					{generating ? "Generating…" : "Generate suggestions"}
				</Button>
				<Stack direction="row" alignItems="center" spacing={1}>
					{Boolean(campaigns.length) && (
						<>
							<ToggleButtonGroup exclusive size="small" value={adPreview} onChange={(_, next) => next && setAdPreview(next)}>
								<ToggleButton value="mobile" sx={previewToggleSx}>
									Mobile
								</ToggleButton>
								<ToggleButton value="tablet" sx={previewToggleSx}>
									Tablet
								</ToggleButton>
								<ToggleButton value="pc" sx={previewToggleSx}>
									PC
								</ToggleButton>
							</ToggleButtonGroup>
							<Tooltip title="Download markdown">
								<IconButton size="small" onClick={downloadCampaigns} sx={{ color: grey[700] }}>
									<FileDownloadRounded fontSize="small" />
								</IconButton>
							</Tooltip>
						</>
					)}
				</Stack>
			</Stack>

			<Typography fontWeight={700}>Suggestions</Typography>
			{!campaigns.length && !generating && (
				<Alert severity="info">Generate RSA campaign suggestions from Explore keywords.</Alert>
			)}
			{campaigns.map((campaign) => (
				<Paper
					key={campaign.campaignName}
					variant="outlined"
					sx={{ p: 2, borderRadius: 2, borderLeft: "4px solid #175CD3", overflow: "hidden" }}>
					<Typography fontWeight={800} sx={{ wordBreak: "break-word" }}>
						Campaign: {campaign.campaignName}
					</Typography>
					{(campaign.adGroups || []).map((group) => (
						<Box key={group.name} mt={2} ml={1}>
							<Typography fontWeight={600} sx={{ wordBreak: "break-word" }}>
								Ad Group: {group.name}
							</Typography>
							<Typography variant="body2" color="text.secondary" mb={1.5} sx={{ wordBreak: "break-word" }}>
								<strong>Keywords:</strong> {(group.keywords || []).join(", ")}
							</Typography>
							<Box
								sx={{
									display: "grid",
									gap: 1.5,
									gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
								}}>
								{(group.ads || []).map((ad, index) => (
									<GigaAdPreview
										key={`${group.name}-${index}`}
										ad={ad}
										maxWidth={AD_PREVIEW_WIDTH[adPreview]}
										displayUrl={`www.example.com/${slug((group.keywords || [""])[0])}`}
									/>
								))}
							</Box>
						</Box>
					))}
				</Paper>
			))}

			<Divider />

			<Typography fontWeight={700}>Creation</Typography>
			<TextField
				size="small"
				label="Campaign keywords"
				value={creationKeywords}
				onChange={(e) => setCreationKeywords(e.target.value)}
				placeholder="keyword1, keyword2"
				sx={fieldSx}
			/>
			<TextField
				multiline
				minRows={3}
				label="Instructions"
				value={instructions}
				onChange={(e) => setInstructions(e.target.value)}
				placeholder="Your additional instructions (e.g. Created ads in German)."
				InputProps={{
					endAdornment: (
						<IconButton size="small" onClick={() => setInfo("instructions")} sx={{ alignSelf: "flex-start" }}>
							<InfoOutlined sx={{ fontSize: 16, color: grey[500] }} />
						</IconButton>
					),
				}}
				sx={fieldSx}
			/>
			<Box>
				<Button
					variant="contained"
					startIcon={creatingAds ? <CircularProgress size={16} color="inherit" /> : <AddCircleOutlineRounded />}
					onClick={() => onCreateAds({ brandName, styleGuide, creationKeywords, instructions })}
					disabled={creatingAds || generating || !creationKeywords.trim()}
					sx={quietBtnSx}>
					{creatingAds ? "Creating…" : "Generate ad"}
				</Button>
			</Box>
			{createdAds.length > 0 && (
				<Box
					sx={{
						display: "grid",
						gap: 2,
						gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
					}}>
					{createdAds.map((ad, index) => (
						<Box key={index}>
							<Typography variant="body2" color="text.secondary" mb={1}>
								Option {index + 1}
							</Typography>
							<GigaAdPreview ad={ad} maxWidth={AD_PREVIEW_WIDTH[adPreview]} />
						</Box>
					))}
				</Box>
			)}

			<Dialog open={Boolean(info)} onClose={() => setInfo("")} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
				<DialogTitle sx={{ fontWeight: 700 }}>
					{info === "brand" ? "Brand name" : info === "instructions" ? "Instructions" : "Google Ads account"}
				</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
						{info === "brand"
							? "Included in campaign suggestions so copy is tailored to your brand."
							: info === "instructions"
							? "Custom instructions such as language, tone, or marketing guidelines for generated ads."
							: "This is the Ads account selected on Explore. Rarebox Campaigns does not pull live RSA performance from Ads; that GIGA-repo step needs Ads API. Suggestions still use this run’s Keyword Planner data."}
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={() => setInfo("")} sx={quietBtnSx}>
						Close
					</Button>
				</DialogActions>
			</Dialog>
		</Stack>
	);
};

export default GigaCampaignsView;
